import {
  Component,
  ComponentInterface,
  h,
  Host,
  Prop,
  State,
  Watch,
  Element,
  Event,
  EventEmitter,
  Method,
} from '@stencil/core';
import { getOffset } from '../../utils/dom';
import { debounce } from '../../utils/throttle';

function _getScrollTop(ele: HTMLElement | Document) {
  return ele instanceof Document ? window.pageYOffset : ele.scrollTop;
}

function _getOffset(ele: HTMLElement, parent: HTMLElement | Document) {
  const parentEle =
    parent instanceof Document ? document.documentElement : parent;
  return getOffset(ele, parentEle);
}

type Positions = 'top' | 'bottom' | 'start' | 'end';

/**
 * The Sticker component is a toolbox for 'sticking' items to scrolling containers.
 *
 */
@Component({
  tag: 'nano-sticker',
  styleUrl: 'sticker.scss',
  shadow: true,
})
export class Sticker implements ComponentInterface {
  private isRtl: boolean = false;
  private io: IntersectionObserver;
  private contentSizeObserver: ResizeObserver;
  private parentSizeObserver: ResizeObserver;
  private sticker: HTMLDivElement;
  private content: HTMLDivElement;
  private scrollPosCache = 0;
  private scrollPosThresholdCache = 0;
  private cacheOffset: number | { v: number; h: number } = 0;
  private slottedContent: Element[];
  private pauseResizeWatcher = false;
  private listenForScrollParent = false;
  private stickToEleInitSize?: DOMRect;
  private positions: Positions[] = [];
  private pauseHide: boolean = false;
  private hasBootstrapped = false;

  @Element() host: HTMLNanoStickerElement;

  // Internal State

  @State() isStuck = false;
  @State() isRootSticker = true;
  @State() hide = false;
  @State() scrollHide = false;
  @State() multiStickerHide = false;
  @State() scrollingTo = false;
  @State() quietModeIsOn = false;
  @State() stuckCounter = 0;
  @State() stickerIndex = 0;
  @State() stickToEle?: HTMLNanoStickerElement;
  @State() triggerPos: { top: number; left: number };
  @State() trigger: HTMLDivElement;
  @State() _offset: number | { v: number; h: number } = 0;

  // Public API

  /** Only applicable to root stickers. When applied the sticker will grow or shrink to the scrollParent size */
  @Prop() autoResize = true;

  /** Manually enable / disabled sticky behaviour */
  @Prop({ mutable: true }) isSticky = true;

  /** Distance the item should be stuck from the sticky edge. */
  @Prop({ mutable: true }) offset: number | { v: number; h: number } = 0;

  /** The edge of the scroll-parent to stick to */
  @Prop() position: 'top' | 'bottom' = 'top';
  // other opts I'd like one day. 'start' | 'end' | 'top-start' | 'top-end' | 'bottom-start' | 'bottom-end'

  /** When applied, the sticker will hide when the scroll-parent
   * is scrolling away from the sticker's stuck position and show when scrolling toward it.
   * By default this will apply when the parent is below either the h or w dimensions */
  @Prop() quietMode: 'on' | 'off' | { h: number; w: number } = {
    h: 600,
    w: 600,
  };

  /** When applied stickers will hide upon new stickers being stuck to the same position */
  @Prop() hideOnNewStickers = true;

  /** The max width of the item to implement sticky behaviour. */
  @Prop() breakPointMax: null | number = null;

  /** The min width of the item to implement sticky behaviour. */
  @Prop() breakPointMin: null | number = null;

  /** Define which scroll element to listen & stick to.
   * By default, it will find traverse the DOM to find the closest. */
  @Prop({ mutable: true }) scrollParent?: HTMLElement | Document;

  /** Upon being stuck, attach the sticker to another sticker element.
   * A JS query selector that returns a <nano-sticker> */
  @Prop() stickTo: string;

  // Broadcasted Events

  /** Emitted when a sticker is stuck */
  @Event() nanoStuck!: EventEmitter<{ sticker: HTMLNanoStickerElement }>;

  /** Emitted when a sticker is unstuck */
  @Event() nanoUnstuck!: EventEmitter<{ sticker: HTMLNanoStickerElement }>;

  /** Emitted when a sticker is hidden */
  @Event() nanoHide!: EventEmitter<{ sticker: HTMLNanoStickerElement }>;

  /** Emitted when a sticker is showing */
  @Event() nanoShow!: EventEmitter<{ sticker: HTMLNanoStickerElement }>;

  // Public methods

  /**
   * Method to return the visibility status of the sticker
   * @returns boolean
   */
  @Method()
  async isHiding() {
    return this.hide;
  }

  /**
   * Method to return the stuck status of the sticker
   * @returns boolean
   */
  @Method()
  async isSticking() {
    this.shouldStick();
    return this.isStuck;
  }

  /** @internal */
  @Method()
  async setTriggerPos(offset: number | { v: number; h: number }) {
    return (this._offset = offset);
  }

  /** @internal */
  @Method()
  async getTriggerPos() {
    return this.triggerPos;
  }

  /** @internal */
  @Method()
  async pauseHiding(pause: boolean) {
    return (this.pauseHide = pause);
  }

  // Watchers

  /** Keep a note of trigger position. Need this to track sticker order in the DOM */
  @Watch('trigger')
  @Watch('scrollParent')
  updateTriggerOffset() {
    if (!this.trigger || !this.scrollParent || !this.listenForScrollParent)
      return;
    this.triggerPos = _getOffset(this.trigger, this.scrollParent);
  }

  /** A root sticker is a fixed element Or this sticker is sticking to another.
   * We need a content resize watcher to make sure we maintain a correct placeholder size in the body */
  @Watch('position')
  stickerResizeListener() {
    if (this.contentSizeObserver) {
      this.contentSizeObserver.disconnect();
      this.contentSizeObserver = undefined;
    }

    this.contentSizeObserver = new ResizeObserver((entries) => {
      for (const entry of entries) {
        if (this.pauseResizeWatcher) return;

        if (entry.contentRect.height) {
          if (this.position === 'top')
            this.host.style.height = entry.contentRect.height + 'px';
          if (this.position === 'bottom')
            this.host.style.minHeight = entry.contentRect.height + 'px';
        }
      }
    });
    this.contentSizeObserver.observe(this.content);
  }

  @Watch('quietMode')
  quietModeChange() {
    if (this.quietMode === 'on')
      requestAnimationFrame(() => (this.quietModeIsOn = true));
    else if (this.quietMode === 'off')
      requestAnimationFrame(() => (this.quietModeIsOn = false));
  }

  /** offset is part of the public API but we also use it to manipulate trigger points */
  @Watch('offset')
  @Watch('_offset')
  setOffset() {
    this.trigger.setAttribute('style', '');
    let v: number, h: number, _v: number, _h: number;
    const sticker: HTMLElement =
      this.isRootSticker || this.stickToEle ? this.sticker : this.host;

    if (!sticker) return;

    if (typeof this.offset === 'object') ({ v, h } = this.offset);
    else v = h = this.offset;

    if (typeof this._offset === 'object') ({ v = _v, h = _h } = this._offset);
    else _v = _h = this._offset;

    const setPos = (pos: 'top' | 'bottom' | 'left' | 'right') => {
      const u = pos.match(/(top|bottom)/) ? v : h;
      setTriggetPos(pos);
      sticker.style[pos] = u + 'px';
      if (u)
        this.host.style.setProperty(
          '--hide-transform-amount',
          `(110% + ${u}px)`
        );
      else this.host.style.setProperty('--hide-transform-amount', null);
    };

    const setTriggetPos = (pos: 'top' | 'bottom' | 'left' | 'right') => {
      const u = pos.match(/(top|bottom)/) ? v + _v : h + _h;
      this.trigger.style[pos] = u * -1 + 'px';
      this.trigger.style.position = 'relative';
      this.trigger.style.height = '1px';
    };

    if (
      (this.positions.includes('start') && this.isRtl) ||
      (this.positions.includes('end') && !this.isRtl)
    )
      setPos('right');

    if (
      (this.positions.includes('end') && this.isRtl) ||
      (this.positions.includes('start') && !this.isRtl)
    )
      setPos('left');

    if (this.positions.includes('top')) setPos('top');
    if (this.positions.includes('bottom')) setPos('bottom');
  }

  @Watch('position')
  positionChange() {
    this.positions = this.position.split('-') as Positions[];
    this.setOffset();
  }

  /** If this sticker is attached to a 'master' sticker (stickToEle),
   * here we move the content of this sticker in and out accordingly */
  @Watch('isStuck')
  async stuckChange() {
    if (this.isStuck) {
      if (this.stickToEle) {
        const content = this.host.children;
        this.slottedContent = Array.from(content);

        this.slottedContent.forEach((child: HTMLElement) => {
          this.stickToEle.appendChild(child);
          child.style.order = this.stickerIndex + '';
        });

        this.nanoStuck.emit({ sticker: this.stickToEle });
      } else this.nanoStuck.emit({ sticker: this.host });
    } else {
      this.scrollHide = false;

      if (this.stickToEle) {
        this.slottedContent.forEach((child: HTMLElement) => {
          this.host.appendChild(child);
          child.style.order = '';
        });
        this.nanoUnstuck.emit({ sticker: this.stickToEle });
      } else this.nanoUnstuck.emit({ sticker: this.host });
    }
  }

  /** attach scroll listener. 'hides' stickers when scrolling away from trigger and 'shows' when scrolling toward */
  @Watch('quietModeIsOn')
  attachScrollListeners() {
    if (this.quietModeIsOn)
      this.scrollParent.addEventListener('scroll', this.onScroll, {
        passive: true,
        capture: false,
      });
    else if (this.scrollParent) {
      this.scrollParent.removeEventListener('scroll', this.onScroll);
      this.moveTrigger(true);
    }
  }

  /** Add / remove parent scrolling listeners. If there's stickToEle we need to remove them */
  @Watch('scrollParent')
  @Watch('stickToEle')
  handleParentEvents(_, oldParent: null | HTMLElement = null) {
    if (!this.listenForScrollParent) return;
    if (oldParent) this.manageListenersOnParent(false, oldParent);
    if (this.scrollParent) this.manageListenersOnParent(!this.stickToEle);
  }

  /** Add remove specific listeners if we're sticking to a sticker.
   * We need to adjust triggers according to the status of the 'master'  */
  @Watch('stickToEle')
  stickToEleChange(_, oldEle: null | HTMLNanoStickerElement) {
    if (this.stickToEle) {
      this.stickToEle.addEventListener('nanoHide', this.onStickToDisplayEvent);
      this.stickToEle.addEventListener('nanoShow', this.onStickToDisplayEvent);
      this.stickToEle.addEventListener('nanoStuck', this.onStickToDisplayEvent);
      this.stickToEle.addEventListener(
        'nanoUnstuck',
        this.onStickToDisplayEvent
      );
    }
    if (oldEle) {
      oldEle.removeEventListener('nanoHide', this.onStickToDisplayEvent);
      oldEle.removeEventListener('nanoShow', this.onStickToDisplayEvent);
      oldEle.removeEventListener('nanoStuck', this.onStickToDisplayEvent);
      oldEle.removeEventListener('nanoUnstuck', this.onStickToDisplayEvent);
    }
  }

  /** Find and set the stickToEle from the public, selector string */
  @Watch('stickTo')
  stickToChange() {
    if (this.stickTo)
      this.stickToEle = this.scrollParent.querySelector(this.stickTo);
    else this.stickToEle = undefined;
  }

  /** 2 potential criteria for hiding. Multi-stickers or quietmode. */
  @Watch('multiStickerHide')
  @Watch('scrollHide')
  visibilityDecisionHandler() {
    if (this.multiStickerHide || this.scrollHide) this.hide = true;
    else this.hide = false;
  }

  @Watch('hide')
  handleHideChange() {
    if (this.hide) this.nanoHide.emit({ sticker: this.host });
    else this.nanoShow.emit({ sticker: this.host });
  }

  @Watch('scrollParent')
  setupIO() {
    if (this.io) {
      this.io.disconnect();
      this.io = undefined;
    }
    if (!this.scrollParent || !this.hasBootstrapped || !this.trigger) return;

    this.isRootSticker = this.scrollParent instanceof Document;
    let root = this.scrollParent;
    if (this.scrollParent instanceof Document) root = null;

    this.io = new window.IntersectionObserver(
      (_) => {
        if (this.pauseResizeWatcher || !this.isSticky) return;
        this.shouldStick(_.slice(-1)[0]);
      },
      { root: root as HTMLElement }
    );

    this.io.observe(this.trigger);
  }

  // Private methods / handlers

  /** Assesses the public API quitemode and works out if it should be applied atm.
   * If 'smallscreen', or there is a breakpointmax / min it will add a resizeobserver to conditionally add behaviour */
  private setupParentResizeListener = () => {
    if (this.parentSizeObserver) {
      this.parentSizeObserver.disconnect();
      this.parentSizeObserver = undefined;
    }

    this.parentSizeObserver = new ResizeObserver((entries) => {
      for (const entry of entries) {
        let height: number, width: number;
        if (entry.contentRect.height) height = entry.contentRect.height;
        if (entry.contentRect.width) width = entry.contentRect.width;

        if (typeof this.quietMode === 'object') {
          if (width < this.quietMode.w || height < this.quietMode.h)
            this.quietModeIsOn = true;
          else this.quietModeIsOn = false;
        }

        if (this.breakPointMin) {
          if (width > this.breakPointMin) this.isSticky = true;
          else this.isSticky = false;
        }

        if (this.breakPointMax) {
          if (width < this.breakPointMax) this.isSticky = true;
          else this.isSticky = false;
        }

        if (this.isRootSticker && this.sticker && this.autoResize)
          this.sticker.style.width = this.host.scrollWidth + 'px';
      }
    });

    const toWatch =
      this.scrollParent instanceof Document
        ? this.scrollParent.documentElement
        : this.scrollParent;
    if (toWatch) this.parentSizeObserver.observe(toWatch);
  };

  /** For stickTo stickers. We listen to the events from the 'master' sticker to ammend the hide/show trigger
   * Main diffs from rootstickers / non is height being on 'master' */
  private onStickToDisplayEvent = (
    e: CustomEvent<{ sticker: HTMLNanoStickerElement }>
  ) => {
    if (e.detail.sticker !== this.stickToEle) return;

    switch (e.type) {
      case 'nanoHide':
        this._offset = 0;
        this.cacheOffset = this.offset;
        this.offset = 0;
        /** trigger moves down, underneath sticker if the main sticker hides */
        if (!this.isStuck) this.moveTrigger(false);

        /** If it's an actual 'position: sticky' element, we need to give the sticker an appropriate height.
         * This minimises content jumping around as items are added */
        if (!this.isRootSticker && this.quietModeIsOn) {
          requestAnimationFrame(() => {
            this.stickToEle.style.minHeight =
              this.stickToEleInitSize.height +
              (this.host.scrollHeight +
                (typeof this.cacheOffset === 'object'
                  ? this.cacheOffset.v
                  : this.cacheOffset)) +
              'px';
            this.stickToEle.setTriggerPos(this.stickToEleInitSize.height * -1);
          });
        }
        break;
      case 'nanoShow':
        this._offset = this.stickToEleInitSize.height;
        this.offset = this.cacheOffset;
        this.moveTrigger(true);

        /** reset master sticker size */
        if (!this.isRootSticker && this.quietModeIsOn) {
          requestAnimationFrame(() => {
            this.stickToEle.style.minHeight = '';
            this.stickToEle.setTriggerPos(0);
          });
        }
        break;
      case 'nanoStuck':
        this.stickToEleInitSize = this.stickToEle.getBoundingClientRect();
        this._offset = this.stickToEleInitSize.height;
        break;
    }
  };

  /** Add or removes event listeners / observers on scrolling parent
   * for when the scrolling parent changes, or, this sticker is sticking to another */
  private manageListenersOnParent(
    addEvents: boolean,
    ele?: HTMLElement | Document
  ) {
    const scrollParent = ele || this.scrollParent;

    if (!scrollParent) return;

    if (!addEvents) {
      try {
        if (this.quietModeIsOn)
          scrollParent.removeEventListener('scroll', this.onScroll);
        scrollParent.removeEventListener('nanoStuck', this.onStickEvent);
        scrollParent.removeEventListener('nanoUnstuck', this.onStickEvent);
      } catch (e) {
        console.error('Events haven`t been added');
      }
    } else {
      scrollParent.addEventListener('nanoStuck', this.onStickEvent);
      scrollParent.addEventListener('nanoUnstuck', this.onStickEvent);

      if (this.quietModeIsOn) this.attachScrollListeners();
    }
    this.setupParentResizeListener();
  }

  /** Handler applied to scrolling parent. When 'hideOnNewStickers' is true, hide this sticker on subsequent stuck stickers.  */
  private onStickEvent = async (
    e: CustomEvent<{ sticker: HTMLNanoStickerElement }>
  ) => {
    const sticker = e.detail ? e.detail.sticker : null;
    if (!sticker || sticker.position !== this.position) return;

    const incomingTriggerPos = await sticker.getTriggerPos();

    if (
      e.type === 'nanoStuck' &&
      sticker !== this.host &&
      sticker.scrollParent === this.host.scrollParent
    ) {
      this.stickerIndex++;

      if (!this.hideOnNewStickers) return;

      if (
        (this.positions.includes('top') &&
          this.triggerPos.top < incomingTriggerPos.top) ||
        (this.positions.includes('bottom') &&
          this.triggerPos.top > incomingTriggerPos.top)
      ) {
        this.multiStickerHide = true;
        this.stuckCounter++;
      }
    }

    if (
      e.type === 'nanoUnstuck' &&
      sticker !== this.host &&
      sticker.scrollParent === this.host.scrollParent
    ) {
      this.stickerIndex--;

      if (!this.hideOnNewStickers) return;

      if (
        (this.positions.includes('top') &&
          this.triggerPos.top < incomingTriggerPos.top) ||
        (this.positions.includes('bottom') &&
          this.triggerPos.top > incomingTriggerPos.top)
      ) {
        this.stuckCounter--;
        if (this.stuckCounter < 1) this.multiStickerHide = false;
      }
    }
  };

  /** Scroll handler applied to scrolling parent. Only applied when quietmode is on.
   * 'hides' stickers when scrolling over 100px away from trigger and 'shows' when scrolling toward */
  private onScroll = () => {
    const y: number = _getScrollTop(this.scrollParent);

    // scrolling up
    if (y < this.scrollPosCache) {
      if (!this.positions.includes('bottom')) this.handleScrollTo();
      else this.handleScrollAway(y);
    }

    // scrolling down
    if (y > this.scrollPosCache) {
      if (!this.positions.includes('bottom')) this.handleScrollAway(y);
      else this.handleScrollTo();
    }
    this.scrollPosCache = y;
  };

  /** Watch for scroll direction. Only applies when quietmode is on.
   * We need to addjust the position of the trigger when the sticker is hidden */
  private handleScrollTo() {
    this.scrollPosThresholdCache = null;
    if (!this.scrollHide) return;

    if (this.scrollingTo !== true) {
      this._offset = this.cacheOffset || this._offset;
    }
    this.scrollingTo = true;
    this.scrollHide = false;
  }

  private handleScrollAway(currScroll: number) {
    if (this.scrollHide) return;

    if (!this.isStuck) return;

    if (this.scrollingTo !== false) {
      this.cacheOffset = this._offset;
      this._offset = 0;
    }
    this.scrollingTo = false;

    if (!this.scrollPosThresholdCache)
      this.scrollPosThresholdCache = currScroll;
    else if (
      !this.pauseHide &&
      Math.abs(currScroll - this.scrollPosThresholdCache) > 100
    )
      this.scrollHide = true;
  }

  private moveTrigger(toInitial?: boolean) {
    if (
      (this.positions.includes('bottom') && !toInitial) ||
      (!this.positions.includes('bottom') && toInitial)
    ) {
      this.host.parentNode.insertBefore(this.trigger, this.host);
    } else
      this.host.parentNode.insertBefore(this.trigger, this.host.nextSibling);
  }

  /**
   * Gets the 'closest' scrolling parent
   * @returns either an element with `overflow: scroll | auto` or the parent Document
   */
  private getScrollParent(): HTMLElement | Document {
    const regex = /(auto|scroll)/;
    const parents = (_node: Node, ps: Node[]) => {
      if (_node.parentNode === null) {
        return ps;
      }
      return parents(_node.parentNode, ps.concat([_node]));
    };
    const style = (_node, prop) =>
      getComputedStyle(_node, null).getPropertyValue(prop);
    const overflow = (_node) =>
      style(_node, 'overflow') +
      style(_node, 'overflow-y') +
      style(_node, 'overflow-x');
    const scroll = (_node) => regex.test(overflow(_node));
    const docHeight = document.documentElement.getBoundingClientRect().height;

    const scrollParent = (_node: HTMLElement) => {
      if (!(_node instanceof HTMLElement)) return;

      const ps = parents(_node, []);
      return (
        ps.find(
          (ele) =>
            scroll(ele) && ele.getBoundingClientRect().height !== docHeight
        ) || document
      );
    };

    return scrollParent(this.host);
  }

  private shouldStick(data?: IntersectionObserverEntry) {
    let scrollAmt: number;

    if (this.positions.includes('top')) {
      if (!this.isRootSticker)
        scrollAmt = _getOffset(this.trigger, this.scrollParent).top;
      else scrollAmt = this.trigger.getBoundingClientRect().top;
      this.isStuck = scrollAmt < -1;
    } else if (this.positions.includes('bottom')) {
      scrollAmt = this.trigger.getBoundingClientRect().top;
      const parentBounding =
        data && data.rootBounds
          ? data.rootBounds
          : (this.scrollParent instanceof Document
              ? document.documentElement
              : this.scrollParent
            ).getBoundingClientRect();
      this.isStuck = scrollAmt > parentBounding.height + parentBounding.top;
    }
  }

  private bootstrapGurantor() {
    this.hasBootstrapped = true;

    this.scrollParent = this.scrollParent || this.getScrollParent();
    this.isRootSticker = this.scrollParent instanceof Document;

    this.trigger = this.trigger || document.createElement('div');
    this.trigger.classList.add('sticker-trigger');

    this.positionChange();
    this.moveTrigger(true);
    this.quietModeChange();

    this.listenForScrollParent = true;
    this.handleParentEvents(true);

    this.stickerIndex = Array.from(
      this.scrollParent.querySelectorAll('nano-sticker')
    )
      .filter(
        (sticker: HTMLNanoStickerElement) => sticker.position === this.position
      )
      .findIndex((sticker) => sticker === this.host);

    this.setOffset();
    this.stickerResizeListener();
    this.setupParentResizeListener();

    // bit of a hack ... add default 'order'. Required for 'stickTo' elements.
    this.slottedContent = Array.from(this.host.children);
    this.slottedContent.forEach((child: HTMLElement) => {
      child.style.order = this.stickerIndex + '';
    });

    this.updateTriggerOffset();
    this.stickToChange();
    this.setupIO();
    this.onStickToDisplayEvent = debounce(this.onStickToDisplayEvent, 50);
  }

  // Stencil metthods

  connectedCallback() {
    this.isRtl =
      this.host.dir === 'rtl' ||
      (this.host.ownerDocument as Document).dir === 'rtl';

    // this is all horrible.... shrug
    document.documentElement.addEventListener('nanoComponentsReady', () => {
      setTimeout((_) => this.bootstrapGurantor(), 200);
    });
    setTimeout((_) => {
      if (!this.hasBootstrapped) this.bootstrapGurantor();
    }, 1000);
  }

  disconnectedCallback() {
    if (this.io) {
      this.io.disconnect();
      this.io = undefined;
    }

    if (this.parentSizeObserver) {
      this.parentSizeObserver.disconnect();
      this.parentSizeObserver = undefined;
    }

    this.scrollParent = null;
    this.hasBootstrapped = false;
  }

  render() {
    return (
      <Host
        sticky={!this.isRootSticker && !this.stickToEle && this.isSticky}
        hide={this.hide && this.isStuck}
        siblings={this.stuckCounter}
        index={this.stickerIndex}
        stuck={this.isStuck && this.isSticky}
        placed-bottom={this.positions.includes('bottom')}
        placed-top={this.positions.includes('top')}
        placed-end={this.positions.includes('end')}
        placed-start={this.positions.includes('start')}
      >
        <div
          class={{
            sticker: true,
            sticky: this.isRootSticker && this.isSticky,
            stuck: this.isStuck && this.isRootSticker && this.isSticky,
            hide: this.isRootSticker && this.hide && this.isStuck,
          }}
          ref={(div) => (this.sticker = div)}
        >
          <div class="sticker-content" ref={(div) => (this.content = div)}>
            <slot />
          </div>
        </div>
      </Host>
    );
  }
}
